import React, { useState } from "react";
import { notifications } from "@mantine/notifications";
import { Mail, MessageSquare, Send } from "lucide-react";
import { Endpoints } from "../../../lib/endpoints";
import { PerformRequest } from "../../../lib/usePerformRequest";
import { ButtonLoader } from "../../../components/Loader";

export default function Support() {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    subject: "",
    message: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.subject || !formData.message) {
      notifications.show({
        title: "Validation Error",
        message: "Please fill in all fields",
        color: "red",
      });
      return;
    }

    setIsLoading(true);
    try {
      const endpoint = Endpoints.ContactSupport();
      const response = await PerformRequest({
        method: endpoint.method,
        route: endpoint.route,
        data: formData, // Assuming the API expects the body directly, or wraps it. Usually PerformRequest handles it.
        // If the API expects { subject, message } in the body, this is correct.
      });

      if (response.status === 200 || response.status === 201) {
        notifications.show({
          title: "Success",
          message: "Your message has been sent to our support team.",
          color: "green",
        });
        setFormData({ subject: "", message: "" });
      } else {
        notifications.show({
          title: "Error",
          message:
            response.message || "Failed to send message. Please try again.",
          color: "red",
        });
      }
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: "An unexpected error occurred. Please try again.",
        color: "red",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-page__header">
        <h1 className="dashboard-page__title">Help & Support</h1>
        <p className="dashboard-page__subtitle">
          Have a question or issue? Send us a message and we'll get back to you.
        </p>
      </div>

      <div className="dashboard-content">
        <div className="dashboard-card max-w-2xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="form-group">
              <label htmlFor="subject" className="form-label">
                Subject
              </label>
              <div className="input-with-icon">
                <div className="input-icon">
                  <MessageSquare size={18} />
                </div>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="form-input pl-10"
                  placeholder="What can we help you with?"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="message" className="form-label">
                Message
              </label>
              <div className="relative">
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="form-textarea min-h-[150px]"
                  placeholder="Describe your issue or question in detail..."
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="dashboard-button dashboard-button--primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <ButtonLoader />
                ) : (
                  <>
                    <Send size={18} />
                    <span>Send Message</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
          <div className="dashboard-card flex flex-col items-center text-center p-6">
            <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mb-4">
              <Mail size={24} />
            </div>
            <h3 className="font-semibold text-lg mb-2">Email Support</h3>
            <p className="text-gray-500 text-sm mb-4">
              Direct access to our support team for urgent inquiries.
            </p>
            <a
              href="mailto:support@nigerianbar.org.ng"
              className="text-primary hover:underline font-medium"
            >
              support@nigerianbar.org.ng
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
